import { useCallback, useState } from "react";
import { SharedLoginService } from "../../../api/sharedLogin.service";
import { getDashErrorCode } from "../../FlightAlerts/handler/apiError.helper";
import { OtpOutcome, VerifyOtpOutcome } from "../types";

export interface UseSharedLoginOtpResult {
  sendOtp: (phoneNumber: string) => Promise<OtpOutcome>;
  verifyOtp: (phoneNumber: string, code: string) => Promise<VerifyOtpOutcome>;
  isSending: boolean;
  isVerifying: boolean;
}

export const useSharedLoginOtp = (): UseSharedLoginOtpResult => {
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const sendOtp = useCallback(async (phoneNumber: string): Promise<OtpOutcome> => {
    setIsSending(true);
    try {
      const service = new SharedLoginService();
      await service.sendOtp(phoneNumber);
      return { success: true };
    } catch (err) {
      const errorCode = getDashErrorCode(err);
      return errorCode !== undefined ? { success: false, errorCode } : { success: false };
    } finally {
      setIsSending(false);
    }
  }, []);

  const verifyOtp = useCallback(
    async (phoneNumber: string, code: string): Promise<VerifyOtpOutcome> => {
      setIsVerifying(true);
      try {
        const service = new SharedLoginService();
        const response = await service.verifyOtp(phoneNumber, code);
        const { verifiedPhoneToken, accountExists } = response.data;
        return {
          success: true,
          verifiedPhoneToken,
          ...(typeof accountExists === "boolean" ? { accountExists } : {}),
        };
      } catch (err) {
        const errorCode = getDashErrorCode(err);
        return errorCode !== undefined ? { success: false, errorCode } : { success: false };
      } finally {
        setIsVerifying(false);
      }
    },
    [],
  );

  return { sendOtp, verifyOtp, isSending, isVerifying };
};
